import { api } from "../scripts/api.js";

/*
=============================================================================
  ENGAGEMENT view (professor) — per-student active minutes
=============================================================================
Reads GET /engagement?class=&from=&to=&tz= (routes/engagement.js), which derives
active minutes from event_table via the forgiving/capped-tail gap rule. Shows a
per-student total for a Mon-Sun week (navigable), with per-assignment and per-week
breakdowns on expand, plus a CSV download. Reps metric for FIN 397 / ConCats.
*/

const TZ_OPTIONS = [
  ["America/Phoenix", "Arizona (MST)"],
  ["America/Los_Angeles", "Pacific"],
  ["America/Denver", "Mountain"],
  ["America/Chicago", "Central"],
  ["America/New_York", "Eastern"],
];

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Monday of the week containing d (local calendar date).
function mondayOf(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

class AppEngagement extends HTMLElement {
  connectedCallback() {
    this.weekOffset = 0;      // 0 = current week, -1 = last week, ...
    this.allTime = false;
    this.classId = null;
    this.inited = false;
    this.lastStudents = [];
    this._open = new Set();

    this.innerHTML = `
      <section class="engagement">
        <header class="eng-head">
          <div>
            <h1>Engagement</h1>
            <p class="eng-sub" id="eng-sub">Active minutes (hands-on-keyboard reps).</p>
          </div>
          <button type="button" class="btn-primary" id="eng-csv">Download CSV</button>
        </header>

        <div class="eng-controls">
          <label class="eng-ctl">Class
            <select id="eng-class"></select>
          </label>
          <label class="eng-ctl">Time zone
            <select id="eng-tz">
              ${TZ_OPTIONS.map(([v, l]) => `<option value="${v}">${l}</option>`).join("")}
            </select>
          </label>
          <div class="eng-week">
            <button type="button" id="eng-prev" class="btn-secondary" aria-label="Previous week">‹</button>
            <span id="eng-week-label" class="eng-week-label">This week</span>
            <button type="button" id="eng-next" class="btn-secondary" aria-label="Next week">›</button>
            <button type="button" id="eng-alltime" class="btn-secondary">All time</button>
          </div>
          <label class="reveal-toggle" title="Off by default so this screen is safe to project. Resets on reload. The CSV always has real names.">
            <input type="checkbox" id="eng-reveal"><span>Show real names</span>
          </label>
          <button type="button" id="eng-save-default" class="btn-secondary eng-save-default" title="Save the current time zone + window as this class's default">Set as class default</button>
        </div>

        <p id="eng-status" class="msg" hidden></p>

        <div class="eng-table-wrap">
          <table class="eng-table">
            <thead>
              <!-- The logged and total columns are added by render() only for a
                   class that has self-logging on, so a FIN 413 report keeps the
                   single column it has always had. -->
              <tr id="eng-head-row"><th class="col-rank">#</th><th>Student</th><th class="col-min">Active min</th></tr>
            </thead>
            <tbody id="eng-body"></tbody>
          </table>
        </div>
        <p id="eng-empty" class="empty" hidden>No activity in this window.</p>
      </section>
    `;

    /* Real names off by default: this report is projected in class. In memory
       only, so a reload puts it back and it cannot be left on for next week.
       The CSV download is unaffected and always carries real names. */
    this.reveal = false;
    this.querySelector("#eng-reveal").addEventListener("change", (e) => {
      this.reveal = e.target.checked;
      this.load();
    });

    this.classSel = this.querySelector("#eng-class");
    this.tzSel = this.querySelector("#eng-tz");
    this.body = this.querySelector("#eng-body");
    this.empty = this.querySelector("#eng-empty");
    this.status = this.querySelector("#eng-status");
    this.weekLabel = this.querySelector("#eng-week-label");

    this.classSel.addEventListener("change", () => { this.classId = Number(this.classSel.value); this.applyClassContext(); });
    this.tzSel.addEventListener("change", () => this.load());
    this.querySelector("#eng-save-default").addEventListener("click", () => this.saveDefault());
    this.querySelector("#eng-prev").addEventListener("click", () => { this.allTime = false; this.weekOffset -= 1; this.load(); });
    this.querySelector("#eng-next").addEventListener("click", () => { this.allTime = false; this.weekOffset += 1; this.load(); });
    this.querySelector("#eng-alltime").addEventListener("click", () => { this.allTime = !this.allTime; this.load(); });
    this.querySelector("#eng-csv").addEventListener("click", () => this.downloadCsv());

    this.body.addEventListener("click", (e) => {
      // Buttons inside an expanded row must not collapse it. They sit in a
      // tr.eng-dd rather than a tr.eng-row so closest() already misses them, but
      // saying so explicitly means a future markup change cannot make the log
      // editor unusable by accident.
      if (e.target.closest("tr.eng-dd")) return;
      const tr = e.target.closest("tr.eng-row");
      if (!tr) return;
      const uid = Number(tr.dataset.uid);
      if (this._open.has(uid)) {
        this._open.delete(uid);
      } else {
        this._open.add(uid);
        if (this.logOfflineEnabled) this.loadStudentLog(uid);
      }
      this.render();
    });

    // Delegated, because the drilldown is rebuilt by render() on every toggle
    // and per-element listeners would be lost or duplicated each time.
    this.body.addEventListener("click", (e) => {
      const save = e.target.closest("[data-plog-save]");
      if (save) return this.saveStudentLog(Number(save.dataset.plogSave));
      const del = e.target.closest("[data-plog-del]");
      if (del) return this.deleteStudentLog(
        Number(del.dataset.plogDel), del.dataset.day, del.dataset.category);
    });
  }

  /* =====================================================================
     PER-STUDENT OFFLINE LOG (professor side)
     ---------------------------------------------------------------------
     The student's own form is limited to the current week and the previous
     one. This one is not: a professor is the escape hatch for a day that has
     closed, for a student who was away, and for a figure that needs
     correcting. The server enforces both rules; this UI only reflects them.

     An entry written here is stamped with the professor as its author, so it
     appears marked "adjusted" in the student's own list. A number that appears
     in someone's record with no visible author is how a student ends up
     arguing with a total nobody will own.
     ===================================================================== */

  async loadStudentLog(uid) {
    this._logs = this._logs || new Map();
    try {
      const res = await api(
        `/engagement/log?class=${encodeURIComponent(this.classId)}&user=${encodeURIComponent(uid)}`);
      const data = await res.json();
      if (res.ok && data.ok) {
        this._logs.set(uid, data);
        this.render();
      }
    } catch {
      /* The drilldown still shows the tracked breakdown; the log block just
         does not appear. Not worth an error banner over the whole report. */
    }
  }

  async saveStudentLog(uid) {
    const q = (id) => this.querySelector(`#plog-${id}-${uid}`);
    const body = {
      class: this.classId,
      user: uid,
      day: q("day").value,
      minutes: Number(q("min").value),
      category: q("cat").value,
      note: q("note").value,
    };
    const msg = q("msg");
    msg.textContent = "Saving...";
    try {
      const res = await api("/engagement/log", { method: "POST", body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        // The day-total refusal is the one worth spelling out: it is a rule
        // about the whole day, so "too large" on its own reads as a complaint
        // about the number just typed.
        msg.textContent = data.reason === "day_total_too_large"
          ? `That day already has ${data.already_logged ?? 0} minutes logged, so there is room for ${data.remaining ?? 0} more.`
          : `Could not save: ${data.reason || "unknown"}${data.detail ? ` (${data.detail})` : ""}`;
        return;
      }
      msg.textContent = "";
      await this.loadStudentLog(uid);
      await this.load();          // the totals in the table just changed
    } catch {
      msg.textContent = "Could not save.";
    }
  }

  async deleteStudentLog(uid, day, category) {
    try {
      // Category-scoped, so removing one activity type leaves the day's other
      // entries alone. Omitting it would clear the whole day, which is a
      // different and much blunter action than the row the professor clicked.
      await api(
        `/engagement/log?class=${encodeURIComponent(this.classId)}&user=${encodeURIComponent(uid)}` +
          `&day=${encodeURIComponent(day)}&category=${encodeURIComponent(category)}`,
        { method: "DELETE" });
      await this.loadStudentLog(uid);
      await this.load();
    } catch {
      /* handled by the next load */
    }
  }

  /** The editor block for one expanded student. Empty string when the class does
   *  not have logging on, so nothing about this appears on a normal class. */
  studentLogHTML(uid) {
    if (!this.logOfflineEnabled) return "";
    const log = this._logs && this._logs.get(uid);
    if (!log) return `<div class="eng-dd-block"><div class="eng-dd-title">Logged offline</div>
      <div class="eng-dd-empty">Loading...</div></div>`;

    const cats = (log.categories || [])
      .map((c) => `<option value="${esc(c.value)}">${esc(c.label)}</option>`).join("");
    const lines = (log.entries || []).length
      ? log.entries.map((e) => `
          <div class="eng-dd-line">
            <span>${esc(e.day)} · ${esc(e.category)}${e.note ? ` · ${esc(e.note)}` : ""}${
              e.adjusted_by_staff ? ` <em>(entered by staff)</em>` : ""}</span>
            <span><b>${e.minutes}</b>
              <button class="btn-secondary eng-plog-del" data-plog-del="${uid}"
                      data-day="${esc(e.day)}" data-category="${esc(e.category)}">remove</button></span>
          </div>`).join("")
      : `<div class="eng-dd-empty">Nothing logged.</div>`;

    // A free date field rather than the student's dropdown of open days: the
    // whole point of this editor is reaching days their window has closed.
    return `
      <div class="eng-dd-block">
        <div class="eng-dd-title">Logged offline: self-reported, one row per activity type per day</div>
        ${lines}
        <div class="eng-plog-form">
          <input type="date" id="plog-day-${uid}" max="${esc(log.today || "")}">
          <input type="number" id="plog-min-${uid}" min="1" max="${log.max_minutes_per_day}"
                 step="1" placeholder="min">
          <select id="plog-cat-${uid}">${cats}</select>
          <input type="text" id="plog-note-${uid}" maxlength="500" placeholder="note (optional)">
          <button class="btn-secondary" data-plog-save="${uid}">Save</button>
        </div>
        <div class="eng-dd-empty" id="plog-msg-${uid}"></div>
      </div>`;
  }

  // Called by the router with an optional classId param (deep link).
  async show(param) {
    if (!this.inited) {
      await this.initClasses();
      this.inited = true;
    }
    if (param != null && String(param) !== "" && Number(param) !== this.classId) {
      this.classId = Number(param);
      this.classSel.value = String(this.classId);
    }
    await this.applyClassContext();
  }

  // Load the class's saved default (tz + window), apply it, then load data.
  async applyClassContext() {
    if (this.classId == null) return;
    try {
      const res = await api(`/engagement/settings?class=${this.classId}`);
      if (res.ok) {
        const s = await res.json();
        if (s.tz) this.tzSel.value = s.tz;
        this.allTime = s.defaultRange === "all";
        this.weekOffset = 0;
      }
    } catch (err) {
      console.error("engagement settings read failed:", err);
    }
    await this.load();
  }

  async saveDefault() {
    if (this.classId == null) return;
    const btn = this.querySelector("#eng-save-default");
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Saving...";
    try {
      const res = await api("/engagement/settings", {
        method: "PUT",
        body: JSON.stringify({
          class: this.classId,
          tz: this.tzSel.value,
          defaultRange: this.allTime ? "all" : "week",
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      btn.textContent = "Saved ✓";
      setTimeout(() => { btn.textContent = prev; btn.disabled = false; }, 1400);
    } catch (err) {
      console.error("save engagement default failed:", err);
      btn.textContent = "Couldn't save";
      setTimeout(() => { btn.textContent = prev; btn.disabled = false; }, 1800);
    }
  }

  async initClasses() {
    try {
      const res = await api("/list-classes");
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      const classes = data.classes ?? [];
      this.classSel.innerHTML = classes
        .map((c) => `<option value="${c.class_id}">${esc(`${c.course_code} · ${c.section}`)}</option>`)
        .join("");
      if (classes.length && this.classId == null) this.classId = Number(classes[0].class_id);
      if (this.classId != null) this.classSel.value = String(this.classId);
    } catch (err) {
      this.setStatus("Could not load classes.", "err");
      console.error(err);
    }
  }

  // Returns { from, to } naive-local date strings for the current selection,
  // or nulls for all-time. Also updates the week label.
  windowParams() {
    if (this.allTime) {
      this.weekLabel.textContent = "All time";
      return { from: null, to: null };
    }
    const mon = addDays(mondayOf(new Date()), this.weekOffset * 7);
    const sun = addDays(mon, 6);
    const nextMon = addDays(mon, 7);
    const rel = this.weekOffset === 0 ? "This week" : this.weekOffset === -1 ? "Last week" : "";
    this.weekLabel.textContent = `${rel ? rel + " · " : ""}${ymd(mon)} – ${ymd(sun)}`;
    return { from: ymd(mon), to: ymd(nextMon) }; // [Mon 00:00, next Mon 00:00)
  }

  queryString() {
    const tz = this.tzSel.value || "America/Phoenix";
    const { from, to } = this.windowParams();
    const p = new URLSearchParams({ class: String(this.classId), tz });
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    return { qs: p.toString(), from, to };
  }

  async load() {
    if (this.classId == null) return;
    this.setStatus("Loading...", "ok");
    const { qs } = this.queryString();
    try {
      const res = await api(`/engagement?${qs}${this.reveal ? "&reveal=1" : ""}`);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      /*
         Sorted on the TOTAL (measured + self-logged), because that is the figure
         the class board ranks on and two screens that order the same students
         differently is a support question waiting to happen. totalMinutes falls
         back to activeMinutes so a server that predates this field still sorts.
      */
      const tot = (s) => (s.totalMinutes != null ? s.totalMinutes : s.activeMinutes);
      this.lastStudents = (data.students ?? []).slice().sort((a, b) => tot(b) - tot(a));
      // Any logged time at all in this window decides whether the extra columns
      // appear. Driven by the data rather than by a flag lookup, so a report run
      // over a window from before the feature was switched on shows the plain
      // table it should.
      this.hasLogged = this.lastStudents.some((s) => (s.loggedMinutes || 0) > 0);
      // From the class flag, NOT from whether anyone has logged anything: a
      // class switched on this morning has no entries yet and is exactly the
      // case where the professor needs the editor.
      this.logOfflineEnabled = data.logOfflineEnabled === true;
      this.setStatus(null);
      this.render();
    } catch (err) {
      this.setStatus("Could not load engagement.", "err");
      console.error(err);
    }
  }

  render() {
    const rows = this.lastStudents;
    const split = this.hasLogged === true;
    this.empty.hidden = rows.length > 0;

    // "Active min" keeps meaning MEASURED time in every version of this table.
    // It is not quietly widened to include self-reported minutes, because
    // something downstream of this screen is a gradebook and a column that
    // changes what it counts without changing its name is how a grade dispute
    // starts. Logged and Total arrive as new columns beside it.
    const head = this.querySelector("#eng-head-row");
    if (head) {
      head.innerHTML = `<th class="col-rank">#</th><th>Student</th>` +
        `<th class="col-min">Active min</th>` +
        (split ? `<th class="col-min">Logged</th><th class="col-min">Total</th>` : "");
    }
    const span = split ? 5 : 3;

    this.body.innerHTML = rows.map((s, i) => {
      const open = this._open.has(s.userId);
      const main = `
        <tr class="eng-row ${open ? "is-open" : ""}" data-uid="${s.userId}">
          <td class="col-rank">${i + 1}</td>
          <td>
            <div class="eng-name">${esc(s.name)}</div>
            ${s.email ? `<div class="eng-email">${esc(s.email)}</div>` : ""}
          </td>
          <td class="col-min">${s.activeMinutes}</td>
          ${split
            ? `<td class="col-min eng-logged">${s.loggedMinutes || 0}</td>
               <td class="col-min"><b>${s.totalMinutes != null ? s.totalMinutes : s.activeMinutes}</b></td>`
            : ""}
        </tr>`;
      if (!open) return main;

      const asg = (s.byAssignment || []).length
        ? `<div class="eng-dd-block"><div class="eng-dd-title">By assignment</div>${
            s.byAssignment.map((a) => `<div class="eng-dd-line"><span>${esc(a.title || a.assignmentId)}</span><b>${a.activeMinutes}</b></div>`).join("")
          }</div>`
        : "";
      const wk = (s.byWeek || []).length
        ? `<div class="eng-dd-block"><div class="eng-dd-title">By week (Mon start)</div>${
            s.byWeek.map((w) => {
              // The week's split is shown only where there is a split to show,
              // so a week of purely measured time reads exactly as it did before.
              const lg = w.loggedMinutes || 0;
              const detail = lg > 0
                ? ` <span class="eng-dd-split">(${w.trackedMinutes} measured + ${lg} logged)</span>`
                : "";
              const shown = lg > 0 ? w.totalMinutes : w.activeMinutes;
              return `<div class="eng-dd-line"><span>${esc(w.weekStart)}${detail}</span><b>${shown}</b></div>`;
            }).join("")
          }</div>`
        : "";
      // By assignment is MEASURED time only and says so, because self-logged
      // practice belongs to no assignment: a student who competed on another
      // platform was not in a workbook this platform knows about, and inventing
      // one to hang the minutes from would be a lie in the one view built for
      // drilling into a specific file.
      const logBlock = this.studentLogHTML(s.userId);
      const detail = `
        <tr class="eng-dd"><td colspan="${span}">
          ${asg || wk || logBlock
            ? asg + wk + logBlock
            : `<div class="eng-dd-empty">No activity in this window.</div>`}
        </td></tr>`;
      return main + detail;
    }).join("");
  }

  async downloadCsv() {
    if (this.classId == null) return;
    const { qs } = this.queryString();
    const btn = this.querySelector("#eng-csv");
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Preparing...";
    try {
      const res = await api(`/engagement?${qs}&format=csv`);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `engagement_class${this.classId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      this.setStatus("Could not export CSV.", "err");
      console.error(err);
    } finally {
      btn.disabled = false;
      btn.textContent = prev;
    }
  }

  setStatus(msg, kind = "err") {
    if (!msg) { this.status.hidden = true; this.status.textContent = ""; return; }
    this.status.hidden = false;
    this.status.className = `msg ${kind}`;
    this.status.textContent = msg;
  }
}

customElements.define("app-engagement", AppEngagement);
